// frontend/src/motion/useReveal.js
//
// The reveal-on-scroll hook for the live-mesh homepage - the React port of the
// prototype's `[data-reveal]` pattern.
//
// It ARMS NOTHING in two cases, and both are corrections rather than
// conveniences:
//
//   * window.__PRERENDER__. scripts/prerender.js drives the page with puppeteer
//     at its default 800x600 and never scrolls, so today every below-the-fold
//     section's observer stays un-fired and the snapshot we serve bakes
//     `opacity: 0` into sections 3-9. innerText still clears the 200-char gate,
//     so nothing catches it. Forcing the revealed state here makes the snapshot
//     content-complete AND visible with JS disabled.
//   * motion off. A visitor who asked for no animation should see the content,
//     not a faded-in version of it a frame later.
//
// Usage:
//   const { ref, className } = useReveal();
//   <Box ref={ref} className={className}>…</Box>
//
// `className` is "hl-reveal" while armed and "hl-reveal hl-in" once revealed;
// when nothing is armed it is "" so the element carries no opacity rule at all.
// The CSS lives in src/homepage/homepage.css.

import { useMemo } from "react";
import useInViewGate, { isPrerendering } from "./useInViewGate";
import useMotion from "./useMotion";
import useHydrated from "./useHydrated";

/**
 * @param {object} [options]
 * @param {"up"|"left"|"scale"} [options.variant] entrance direction.
 * @param {number} [options.threshold] fraction visible before revealing.
 * @param {string} [options.rootMargin] IntersectionObserver rootMargin.
 * @returns {{ ref: (node: Element|null) => void, revealed: boolean, className: string }}
 */
export default function useReveal(options = {}) {
  const { variant = "up", threshold = 0.15, rootMargin = "0px" } = options;
  const { off } = useMotion();

  // `once: true` - a reveal is an entrance, not a toggle. Re-hiding a section a
  // visitor scrolled back past would be a bug, not an effect.
  const [ref, inView] = useInViewGate({ threshold, rootMargin, once: true });
  // The prerendered snapshot carries NO reveal class (it was taken disarmed);
  // the hydrating client's first render must say the same, and arms one
  // render later. A client-only boot is armed from the first render.
  const hydrated = useHydrated();

  const disarmed = off || isPrerendering() || !hydrated;
  const revealed = disarmed || inView;

  const className = useMemo(() => {
    if (disarmed) return "";
    const base = variant === "left" ? "hl-reveal hl-reveal-left"
      : variant === "scale" ? "hl-reveal hl-reveal-scale"
      : "hl-reveal";
    return revealed ? `${base} hl-in` : base;
  }, [disarmed, revealed, variant]);

  return { ref, revealed, className };
}
